import React, { ChangeEvent, forwardRef, MutableRefObject, useEffect, useRef, useCallback } from 'react';
import { FormError } from '../types/ErrorResponse.type';
import { ErrorIcon } from './ErrorIcon';
import { FieldErrors } from './FieldErrors';

type Props = {
	name: string;
	className: string;
	placeholder?: string;
	icon?: JSX.Element;
	label?: string;
	value?: string;
	onChange: (val: string) => void;
	errors?: FormError;
	disabled?: boolean;
	onFocus?: () => void;
	maxLen?: number;
	autoResize?: boolean;
};

export const Textarea = forwardRef(
	(
		{
			name,
			label,
			placeholder,
			value,
			onChange,
			errors,
			className,
			icon,
			disabled = false,
			onFocus,
			maxLen,
			autoResize = false,
		}: Props,
		ref: ((instance: unknown) => void) | MutableRefObject<any> | null
	) => {
		const textareaRef = useRef<HTMLTextAreaElement | null>(null);

		// Auto-resize function
		const adjustHeight = useCallback(() => {
			if (autoResize && textareaRef.current) {
				const textarea = textareaRef.current;
				// Reset height to auto to get the natural height
				textarea.style.height = 'auto';
				// Set height to scrollHeight, with a minimum height
				const newHeight = Math.max(textarea.scrollHeight, 40); // 40px minimum
				textarea.style.height = `${newHeight}px`;
			}
		}, [autoResize]);

		const onChangeValue = (e: ChangeEvent<HTMLTextAreaElement>) => {
			const val = e.target.value;
			onChange(val);
			
			// Auto-resize functionality - use the same function for consistency
			if (autoResize) {
				// Use setTimeout to ensure the value is updated in the DOM first
				setTimeout(adjustHeight, 0);
			}
		};

		// Auto-resize on value change
		useEffect(() => {
			adjustHeight();
		}, [value, autoResize, adjustHeight]);

		// Auto-resize on component mount and when autoResize changes
		useEffect(() => {
			if (autoResize) {
				// Use setTimeout to ensure DOM is fully rendered
				const timer = setTimeout(adjustHeight, 0);
				return () => clearTimeout(timer);
			}
		}, [autoResize, adjustHeight]);

		// Additional effect to handle any missed updates
		useEffect(() => {
			if (autoResize && value) {
				// Use requestAnimationFrame to ensure it runs after any DOM updates
				const frameId = requestAnimationFrame(() => {
					adjustHeight();
				});
				return () => cancelAnimationFrame(frameId);
			}
		}, [value, autoResize, adjustHeight]);

		return (
			<div className={className}>
				{label && (
					<div>
						<label
							htmlFor={name}
							className="block text-sm font-medium leading-5 text-gray-900 sm:mt-px sm:pt-2"
						>
							{label}
						</label>
					</div>
				)}
				<div>
					<div className="relative rounded-md shadow-sm">
						<div className="relative flex items-stretch flex-grow focus-within:z-10">
							{icon && (
								<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
									{icon}
								</div>
							)}
							<textarea
								id={name}
								name={name}
								placeholder={placeholder}
								className={[
									'block w-full border-gray-300 rounded-md focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition duration-150 ease-in-out sm:text-sm sm:leading-5',
									errors
										? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:ring-red'
										: '',
									icon ? 'pl-10' : '',
									autoResize ? 'resize-none overflow-hidden' : '',
								].join(' ')}
								value={value || ''}
								onChange={onChangeValue}
								onFocus={onFocus}
								disabled={disabled}
								ref={(node) => {
									textareaRef.current = node;
									if (typeof ref === 'function') {
										ref(node);
									} else if (ref) {
										(ref as any).current = node;
									}
								}}
								maxLength={maxLen}
							/>
							{errors && <ErrorIcon />}
						</div>
					</div>
					<FieldErrors errors={errors} />
				</div>
			</div>
		);
	}
);
